import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import ResultModal from './ResultModal';
import TuPreferesReveal from './TuPreferesReveal';
import type { TuPreferesCard } from '../data/soft';

type SoftCategory = 'action' | 'tuPreferes' | 'battle';

interface SoftModeGameProps {
  players: string[];
  actions: string[];
  tuPreferes: TuPreferesCard[];
  battles: string[];
}

const SLOT_DURATION = 1200;
const SLOT_TICKS = 16;

const DEFAULT_TU_PREFERES: TuPreferesCard = {
  optionA: 'Option A à venir...',
  optionB: 'Option B à venir...',
};

export default function SoftModeGame({ players, actions, tuPreferes, battles }: SoftModeGameProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSlotSpinning, setIsSlotSpinning] = useState(true);
  const [slotDisplayName, setSlotDisplayName] = useState('');
  const [cardsVisible, setCardsVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<SoftCategory | null>(null);
  const [resultContent, setResultContent] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [tappedCard, setTappedCard] = useState<SoftCategory | null>(null);
  const [showTuPreferes, setShowTuPreferes] = useState(false);
  const [currentTpCard, setCurrentTpCard] = useState<TuPreferesCard | null>(null);
  const slotTimers = useRef<number[]>([]);
  const turnCount = useRef(0);

  const shuffledOrder = useMemo(() => {
    const indices = players.map((_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  }, [players]);

  const currentPlayer = players[shuffledOrder[currentIndex % shuffledOrder.length]] || '';

  const getRandomItem = useCallback((items: string[]): string => {
    if (items.length === 0) return 'Contenu à venir... 🎉';
    return items[Math.floor(Math.random() * items.length)];
  }, []);

  const getRandomTpCard = useCallback((): TuPreferesCard => {
    if (tuPreferes.length === 0) return DEFAULT_TU_PREFERES;
    return tuPreferes[Math.floor(Math.random() * tuPreferes.length)];
  }, [tuPreferes]);

  const runSlotMachine = useCallback((targetName: string) => {
    setIsSlotSpinning(true);
    setCardsVisible(false);
    setSelectedCategory(null);
    setTappedCard(null);
    setShowTuPreferes(false);
    setCurrentTpCard(null);

    const interval = SLOT_DURATION / SLOT_TICKS;
    for (let i = 0; i < SLOT_TICKS; i++) {
      const timerId = window.setTimeout(() => {
        if (i < SLOT_TICKS - 1) {
          const randomPlayer = players[Math.floor(Math.random() * players.length)];
          setSlotDisplayName(randomPlayer);
        } else {
          setSlotDisplayName(targetName);
          setIsSlotSpinning(false);
          setTimeout(() => setCardsVisible(true), 200);
          try { Haptics.impact({ style: ImpactStyle.Medium }); } catch {}
        }
      }, interval * i + (i > SLOT_TICKS * 0.7 ? (i - SLOT_TICKS * 0.7) * 30 : 0));
      slotTimers.current.push(timerId);
    }
  }, [players]);

  useEffect(() => {
    const timers = slotTimers.current;
    runSlotMachine(currentPlayer);
    return () => {
      timers.forEach(id => window.clearTimeout(id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCardTap = useCallback((category: SoftCategory) => {
    if (selectedCategory || isSlotSpinning) return;

    setTappedCard(category);
    try { Haptics.impact({ style: ImpactStyle.Light }); } catch {}

    setTimeout(() => {
      setSelectedCategory(category);

      if (category === 'tuPreferes') {
        setCurrentTpCard(getRandomTpCard());
        setShowTuPreferes(true);
      } else {
        const content = category === 'action'
          ? getRandomItem(actions)
          : getRandomItem(battles);
        setResultContent(content);
        setShowResult(true);
      }
    }, 300);
  }, [selectedCategory, isSlotSpinning, actions, battles, getRandomItem, getRandomTpCard]);

  const handleCloseResult = () => {
    setShowResult(false);
  };

  const handleNextTurn = () => {
    setShowResult(false);
    setShowTuPreferes(false);
    setCurrentTpCard(null);
    setResultContent(null);
    setSelectedCategory(null);
    setTappedCard(null);
    turnCount.current += 1;
    const nextIndex = (currentIndex + 1) % players.length;
    setCurrentIndex(nextIndex);
    const nextPlayer = players[shuffledOrder[nextIndex % shuffledOrder.length]] || '';
    runSlotMachine(nextPlayer);
  };

  const categoryToChoiceType = (cat: SoftCategory) => {
    return cat === 'tuPreferes' ? 'question' as const : 'action' as const;
  };

  const categoryBadgeLabel: Record<SoftCategory, string> = {
    action: 'Action ⚡',
    tuPreferes: 'Tu préfères 🤔',
    battle: 'Battle ⚔️',
  };

  if (showTuPreferes && currentTpCard) {
    return (
      <TuPreferesReveal
        card={currentTpCard}
        playerName={currentPlayer}
        players={players}
        onDone={handleNextTurn}
      />
    );
  }

  return (
    <div className="h-screen bg-gradient-to-b from-blue-800 via-blue-900 to-[#0a1628] relative flex flex-col safe-top safe-bottom">
      {/* Turn counter */}
      <div className="absolute right-6 z-10" style={{ top: 'max(env(safe-area-inset-top, 0px) + 8px, 24px)' }}>
        <span className="text-white/30 font-body text-sm">Tour {turnCount.current + 1}</span>
      </div>

      {/* Top section: player name */}
      <div className="flex-shrink-0 pt-16 pb-4 px-6 text-center">
        <p className="text-blue-300/60 font-body text-sm mb-2 tracking-wide">C'est au tour de...</p>
        <div className="h-16 flex items-center justify-center overflow-hidden">
          <h1
            key={slotDisplayName}
            className={`font-display font-black text-5xl text-white drop-shadow-lg ${
              isSlotSpinning ? 'slot-tick' : 'slot-land'
            }`}
          >
            {slotDisplayName}
          </h1>
        </div>
        <div className={`w-12 h-1 rounded-full mx-auto mt-3 transition-all duration-500 ${
          isSlotSpinning ? 'bg-blue-500/30 w-8' : 'bg-green-400/60 w-12'
        }`} />
      </div>

      {/* Center: 3 cards */}
      <div className="flex-1 flex items-center justify-center px-4 pb-6">
        <div className="flex items-end justify-center gap-3 w-full max-w-sm" style={{ perspective: '800px' }}>
          {/* Card: ACTION */}
          <div
            className={`soft-card soft-card-stagger-1 ${cardsVisible ? 'soft-card-visible' : ''} ${
              tappedCard === 'action' ? 'soft-card-selected' : ''
            } ${tappedCard && tappedCard !== 'action' ? 'opacity-40' : ''}`}
            style={{ transform: cardsVisible ? 'rotate(-6deg)' : undefined, zIndex: 1 }}
            onClick={() => handleCardTap('action')}
          >
            <div className="soft-card-inner action-card">
              <div className="absolute inset-0 flex items-center justify-center opacity-[0.08] text-8xl pointer-events-none">⚡</div>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="font-display font-black text-2xl text-white tracking-wider" style={{ transform: 'rotate(-25deg)' }}>
                  ACTION
                </span>
              </div>
              <div className="absolute bottom-3 left-0 right-0 text-center">
                <span className="text-green-300/50 text-xs font-body">⚡</span>
              </div>
            </div>
          </div>

          {/* Card: TU PRÉFÈRES (center, larger) */}
          <div
            className={`soft-card soft-card-center soft-card-stagger-2 ${cardsVisible ? 'soft-card-visible' : ''} ${
              tappedCard === 'tuPreferes' ? 'soft-card-selected' : ''
            } ${tappedCard && tappedCard !== 'tuPreferes' ? 'opacity-40' : ''}`}
            style={{ zIndex: 2 }}
            onClick={() => handleCardTap('tuPreferes')}
          >
            <div className="soft-card-inner prefer-card">
              <div className="absolute inset-0 flex items-center justify-center opacity-[0.07] text-7xl pointer-events-none">⚖️</div>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <span className="font-display font-black text-xl text-white tracking-wider block leading-tight">TU</span>
                  <span className="font-display font-black text-xl text-white tracking-wider block leading-tight">PRÉFÈRES</span>
                </div>
              </div>
              <div className="absolute bottom-3 left-0 right-0 text-center">
                <span className="text-blue-300/40 text-xs font-body">🤔</span>
              </div>
            </div>
          </div>

          {/* Card: BATTLE */}
          <div
            className={`soft-card soft-card-stagger-3 ${cardsVisible ? 'soft-card-visible' : ''} ${
              tappedCard === 'battle' ? 'soft-card-selected' : ''
            } ${tappedCard && tappedCard !== 'battle' ? 'opacity-40' : ''}`}
            style={{ transform: cardsVisible ? 'rotate(6deg)' : undefined, zIndex: 1 }}
            onClick={() => handleCardTap('battle')}
          >
            <div className="soft-card-inner battle-card">
              <div className="absolute inset-0 flex items-center justify-center opacity-[0.08] text-8xl pointer-events-none">⚔️</div>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="font-display font-black text-2xl text-white tracking-wider" style={{ transform: 'rotate(25deg)' }}>
                  BATTLE
                </span>
              </div>
              <div className="absolute bottom-3 left-0 right-0 text-center">
                <span className="text-blue-300/50 text-xs font-body">⚔️</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Next turn button (shown after result modal closed) */}
      {selectedCategory && selectedCategory !== 'tuPreferes' && !showResult && (
        <div className="flex-shrink-0 px-6 pb-6">
          <button
            onClick={handleNextTurn}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-green-400 to-emerald-500 text-white font-display font-bold text-base btn-press animate-slide-up shadow-[0_4px_20px_rgba(74,222,128,0.3)]"
          >
            Tour suivant →
          </button>
        </div>
      )}

      {/* Result modal — for ACTION and BATTLE only */}
      {selectedCategory && selectedCategory !== 'tuPreferes' && resultContent && (
        <ResultModal
          isOpen={showResult}
          type={categoryToChoiceType(selectedCategory)}
          content={resultContent}
          playerName={currentPlayer}
          mode="soft"
          onClose={handleCloseResult}
          badgeLabel={categoryBadgeLabel[selectedCategory]}
        />
      )}
    </div>
  );
}
